import IconCatch from '@/components/icons/icon-catch/icon-catch';
import Markdown from '@/components/markdown/markdown';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useLoadMoreSources from '@/hooks/use-load-more-sources';
import { RiskFactor, RiskFactorType } from '@/repositories/risk.repository';
import { Box, CloseButton, Divider, Flex, Modal, Text } from '@cmpsr/components';
import { useEffect } from 'react';
import SourceQuoteTag from '../source-quote/source-quote';
import NonQuantifiableRiskFactor from './components/non-quantifiable-risk-factor/non-quantifiable-risk-factor';
import QuantifiableRiskFactor from './components/quantifiable-risk-factor/quantifiable-risk-factor';

type ModalDetailRiskFactor = {
  isOpen: boolean;
  onClose: () => void;
  riskFactor: RiskFactor;
  affectedCancerTypes: string[];
  impactsByCancerType: Record<string, number>;
};

export default function ModalDetailRiskFactor({
  isOpen,
  onClose,
  riskFactor,
  affectedCancerTypes,
  impactsByCancerType,
}: ModalDetailRiskFactor) {
  const analytics = useAnalyticsCalls();

  useEffect(() => {
    if (isOpen) analytics.callRiskFactorViewed(riskFactor.label, riskFactor.id);
  }, [isOpen]);

  const { label, whyItMatters, shortDescription, sources } = riskFactor;

  const { visibleSources, showLoadMoreButton, handleLoadMoreSources } = useLoadMoreSources(isOpen, sources);

  const isNonQuantifiable =
    riskFactor.riskFactorType === RiskFactorType.nonQuantifiablePositive ||
    riskFactor.riskFactorType === RiskFactorType.nonQuantifiableNegative;

  return (
    <Modal isOpen={isOpen} onClose={onClose} trapFocus={false} isCentered>
      <Modal.Overlay bg="background-overlay" backdropFilter="auto" />
      <Modal.Content
        bg="background-page"
        borderRadius={{ lg: 'radii-2' }}
        mt={{ lg: '5rem' }}
        pl={{ base: 'spacer-5', lg: 'spacer-10' }}
        pb={{ base: 'spacer-8', lg: 'spacer-10' }}
        maxW={{ base: 'full', lg: '854px' }}
      >
        <Modal.Header
          display="flex"
          justifyContent="space-between"
          w="full"
          pt={{ lg: 'spacer-4' }}
          pr={{ base: 'spacer-5', lg: 'spacer-4' }}
          px={0}
          pb={0}
          borderRadius={{ lg: 'radii-2' }}
          bg="background-page"
        >
          <Flex direction="column" alignItems="start" pt={{ base: 'spacer-3', lg: 'spacer-6' }} gap={'spacer-2'}>
            <Text variant="text-header-2XL">{label}</Text>
          </Flex>
          <CloseButton
            border="1px solid"
            borderRadius="50%"
            backgroundColor="background-action-default"
            onClick={onClose}
          />
        </Modal.Header>
        <Modal.Body backgroundColor="background-page" py={0} pl={0} pr={{ base: 'spacer-5', lg: 'spacer-10' }}>
          <Flex direction="column" gap="spacer-6">
            <Divider />
            <Flex alignItems="center" gap="spacer-4">
              <IconCatch icon="why-it-matters" size="l" borderRadius="10px" backgroundColor="background-page-03" />
              <Text variant="text-header-L">Why it matters</Text>
            </Flex>
            <Box
              backgroundColor="background-light"
              borderRadius="radii-3"
              px="spacer-6"
              py="spacer-3"
              border="1px solid"
              borderColor="ui-element-divider"
            >
              <Markdown>{whyItMatters}</Markdown>
            </Box>
            {isNonQuantifiable ? (
              <NonQuantifiableRiskFactor />
            ) : (
              <QuantifiableRiskFactor
                shortDescription={shortDescription}
                riskFactor={riskFactor}
                affectedCancerTypes={affectedCancerTypes}
                impactsByCancerType={impactsByCancerType}
              />
            )}
            {sources && sources.length ? <Divider /> : ''}
          </Flex>
        </Modal.Body>
        <Modal.Footer p={0} background="background-page" pr={{ base: 'spacer-5', lg: 'spacer-10' }}>
          {sources.length > 0 && (
            <Flex borderRadius="radii-3" direction="column" w="full">
              <Text textStyle="text-header-l-v" pt="spacer-6" pb="spacer-2">
                The science
              </Text>
              <SourceQuoteTag
                sources={sources.slice(0, visibleSources)}
                showLoadMoreButton={showLoadMoreButton}
                loadMore={handleLoadMoreSources}
              />
            </Flex>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
